/**
 * JourneyContext — shared progress state for the SME journey
 *
 * Tracks: which sections are unlocked, what step the user is on.
 * Drives dynamic sidebar unlocking and context sync (sidebar ↔ active step).
 *
 * Progress levels:
 *   0 — profile only (no assessment yet)
 *   1 — has emissions (DISCOVER unlocked)
 *   2 — has actions (Opportunities + Carbon Value available)
 *   3 — has CARS / pathway (EXECUTE unlocked)
 *   4 — fully complete (all sections)
 */
import React, { createContext, useContext, useState, useEffect } from "react";
import client from "@/api/client";
import { useAuth } from "@/context/AuthContext";

const JourneyContext = createContext({
  progressLevel: 0,
  currentStep:   null,   // 1-10 active step label
  hasEmissions:  false,
  hasActions:    false,
  hasPathway:    false,
  hasCars:       false,
  journeyData:   null,
  setCurrentStep: () => {},
  refreshProgress: () => {},
});

export function JourneyProvider({ children }) {
  const { user } = useAuth();
  const activeRole = user?.active_role || user?.role || "user";
  const [progressLevel, setProgressLevel] = useState(0);
  const [currentStep,   setCurrentStep]   = useState(null);
  const [journeyData,   setJourneyData]   = useState(null);
  const [hasEmissions,  setHasEmissions]  = useState(false);
  const [hasActions,    setHasActions]    = useState(false);
  const [hasPathway,    setHasPathway]    = useState(false);
  const [hasCars,       setHasCars]       = useState(false);

  const loadProgress = async () => {
    if (activeRole !== "sme") return;
    try {
      // Use /me to load current user's real journey data
      const res = await client.get("/sme/my-journey");
      const d   = res.data;
      setJourneyData(d);
      const emiss   = d?.footprint?.total_tco2e > 0;
      const actions = d?.actions?.action_count > 0;
      const cars    = d?.cars_score?.score > 0;
      const pathway = !!d?.pathway?.primary_pathway;
      setHasEmissions(emiss);
      setHasActions(actions);
      setHasCars(cars);
      setHasPathway(pathway);
      // Progress level
      if (pathway && cars)   setProgressLevel(4);
      else if (cars)         setProgressLevel(3);
      else if (actions)      setProgressLevel(2);
      else if (emiss)        setProgressLevel(1);
      else                   setProgressLevel(0);
    } catch {
      // Non-Gasavant user or no data
      setProgressLevel(0);
    }
  };

  useEffect(() => {
    if (activeRole === "sme") {
      loadProgress();
    } else {
      setProgressLevel(0);
      setJourneyData(null);
    }
  }, [activeRole, user]);

  return (
    <JourneyContext.Provider value={{
      progressLevel, currentStep, journeyData,
      hasEmissions, hasActions, hasPathway, hasCars,
      setCurrentStep,
      refreshProgress: loadProgress,
    }}>
      {children}
    </JourneyContext.Provider>
  );
}

export const useJourney = () => useContext(JourneyContext);
